import React, { useState } from 'react';
import { format } from 'date-fns';
import { useGetAvanttiPaymentReportQuery } from '../store/apis/payment';
import { formatCurrency } from '../utils/shared';
import Button from '../components/atoms/Button';
import { parseFriendlyDate } from '../utils/dates';
import TextTitleCard from '../components/atoms/TitleTextCard';
import AdminSectionTitle from '../components/AdminSectionTitle';
import SortableTable from '../components/atoms/SortableTable';
import LoadingLottie from '../components/atoms/LoadingLottie';

interface AmountRow {
  title: string;
  amount: number;
}

interface ProcessedRow {
  title: string;
  amount: number;
}

const AvanttiReportPage: React.FC = () => {
  const [startDate, setStartDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'));
  const [fetchReport, setFetchReport] = useState(false);

  // Skip the query until the user clicks the button
  const {
    data, error, isLoading, isFetching,
  } = useGetAvanttiPaymentReportQuery(
    { startDate, endDate },
    { skip: !fetchReport },
  );

  const handleGenerateReport = () => {
    if (startDate && endDate) {
      setFetchReport(true);
    }
  };

  // Transform data for reported amounts table
  const getReportedAmountsData = () => {
    if (!data) {
      return [];
    }

    const tableData: AmountRow[] = [
      {
        title: 'Igresos Avantti',
        amount: data.avantti_amount,
      },
      {
        title: 'Comisiones MercadoPago',
        amount: data.gateway_amount,
      },
    ];

    return tableData;
  };

  // Transform data for processed amounts table
  const getProcessedAmountsData = () => {
    if (!data) {
      return [];
    }

    const tableData: ProcessedRow[] = [
      {
        title: 'Procesado Online Total',
        amount: data.processed_online_amount,
      },
      {
        title: 'Procesado Online Split',
        amount: data.processed_split_amount,
      },
      {
        title: 'Procesado Online Recibido',
        amount: data.processed_received_amount_online,
      },
      {
        title: 'Procesado Manual',
        amount: data.processed_manual_amount,
      },
      {
        title: 'Total Procesado',
        amount: data.processed_online_amount + data.processed_manual_amount,
      },
    ];

    return tableData;
  };

  const reportedAmountsConfig = [
    {
      label: 'Concepto',
      render: (row: AmountRow) => row.title,
    },
    {
      label: 'Monto',
      render: (row: AmountRow) => formatCurrency(row.amount),
    },
  ];

  const processedAmountsConfig = [
    {
      label: 'Concepto',
      render: (row: ProcessedRow) => row.title,
    },
    {
      label: 'Monto',
      render: (row: ProcessedRow) => formatCurrency(row.amount),
    },
  ];

  return (
    <div className="flex flex-col gap-8 my-6">
      <AdminSectionTitle
        title="Reportes"
        subtitle="Reporte de Pagos Avantti"
      />

      <TextTitleCard title="Filtros de búsqueda">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label htmlFor="startDate" className="block text-sm font-medium text-text-primary mb-1">
              Fecha inicio
            </label>
            <input
              type="date"
              id="startDate"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 border border-border-primary rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          <div>
            <label htmlFor="endDate" className="block text-sm font-medium text-text-primary mb-1">
              Fecha fin
            </label>
            <input
              type="date"
              id="endDate"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 border border-border-primary rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
        </div>

        <Button
          onClick={handleGenerateReport}
          loading={isLoading || isFetching}
          variant="primary"
          className="w-full md:w-auto"
        >
          Consultar
        </Button>
      </TextTitleCard>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">
                Error al cargar los datos del reporte. Por favor intente nuevamente.
              </p>
            </div>
          </div>
        </div>
      )}

      {(isLoading || isFetching) && fetchReport && <LoadingLottie />}

      {data && (
        <div className="flex flex-col gap-6">
          <TextTitleCard
            title="Período del Reporte"
            subtitle={`${parseFriendlyDate(startDate)} a ${parseFriendlyDate(endDate)}`}
          />

          <TextTitleCard
            title="Comisiones"
            subtitle="Ingresos y comisiones reportadas en el período"
          >
            <SortableTable
              data={getReportedAmountsData()}
              config={reportedAmountsConfig}
              keyFn={(row) => row.title}
              pagination={false}
              search={false}
            />
          </TextTitleCard>

          <TextTitleCard
            title="Montos procesados"
            subtitle="Desglose de montos procesados por canal"
          >
            <SortableTable
              data={getProcessedAmountsData()}
              config={processedAmountsConfig}
              keyFn={(row) => row.title}
              pagination={false}
              search={false}
            />
          </TextTitleCard>
        </div>
      )}
    </div>
  );
};

export default AvanttiReportPage;
